import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Plus, Rocket, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

// Lightweight "Today's Targets" widget. Uses /api/targets endpoints.
// Feb 2026 — no longer auto-resets at midnight IST. The list persists
// until the user hits the "Reset list" button (which archives every
// active row). Historical rows stay in the DB with archived:true.
export default function TodaysTargets() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/targets/today");
      setItems(data || []);
    } catch {
      // Silent — non-critical widget
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || adding) return;
    setAdding(true);
    try {
      const { data } = await api.post("/targets", { content });
      setItems((prev) => [...prev, data]);
      setDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add target");
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (t) => {
    const next = !t.completed;
    // Optimistic update
    setItems((prev) => prev.map((x) => x.target_id === t.target_id ? { ...x, completed: next } : x));
    try {
      await api.patch(`/targets/${t.target_id}`, { completed: next });
    } catch {
      // Revert on failure
      setItems((prev) => prev.map((x) => x.target_id === t.target_id ? { ...x, completed: !next } : x));
      toast.error("Couldn't update target");
    }
  };

  const remove = async (t) => {
    setItems((prev) => prev.filter((x) => x.target_id !== t.target_id));
    try {
      await api.delete(`/targets/${t.target_id}`);
    } catch {
      // Refetch on failure
      load();
    }
  };

  const resetAll = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      const { data } = await api.post("/targets/reset");
      setItems([]);
      setConfirmReset(false);
      const n = data?.archived_count || 0;
      toast.success(n === 0 ? "Nothing to reset." : `Reset ${n} target${n === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  const { completed, total } = useMemo(() => {
    const c = items.filter((x) => x.completed).length;
    return { completed: c, total: items.length };
  }, [items]);

  const allDone = total > 0 && completed === total;

  return (
    <div
      className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900"
      data-testid="todays-targets-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-1.5">
            Today&rsquo;s Targets <Rocket size={16} className="text-emerald-500"/>
          </h3>
          <p className="text-xs text-zinc-500">
            {total === 0 ? "Set a few small wins for today." : (
              allDone ? "All done — beast mode 🏆" : `${completed} of ${total} done`
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {total > 0 && (
            <div
              className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400"
              data-testid="todays-targets-counter"
            >
              {completed}/{total}
            </div>
          )}
          {total > 0 && (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              className="rounded-full border border-zinc-200 px-2 py-1 text-[11px] font-medium text-zinc-600 transition hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-red-900/60 dark:hover:bg-red-950/30 dark:hover:text-red-400"
              data-testid="targets-reset-btn"
              title="Reset list — archives all current items"
            >
              <RotateCcw size={11} className="inline -mt-0.5 mr-1"/> Reset
            </button>
          )}
        </div>
      </div>

      {/* Confirmation panel — inline modal (no external Dialog dep needed).
          Explicit two-step: "Are you sure?" → Yes/Cancel. Prevents accidental
          resets on mobile where fat-fingering the button is common. */}
      {confirmReset && (
        <div
          className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs dark:border-red-900/40 dark:bg-red-950/20"
          data-testid="targets-reset-confirm-panel"
        >
          <div className="text-red-900 dark:text-red-200">
            Reset the list? This will archive all {total} current target{total === 1 ? "" : "s"} (completed or not). Your history is preserved.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              onClick={resetAll}
              disabled={resetting}
              className="h-7 bg-red-500 text-white hover:bg-red-600"
              data-testid="targets-reset-confirm-yes"
            >
              {resetting ? "Resetting…" : "Yes, reset"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmReset(false)}
              disabled={resetting}
              className="h-7"
              data-testid="targets-reset-confirm-cancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <form onSubmit={add} className="mt-4 flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Revise Biology Chapter 3"
          maxLength={200}
          className="h-10 text-sm"
          data-testid="target-input"
        />
        <Button
          type="submit"
          disabled={!draft.trim() || adding}
          className="h-10 bg-emerald-500 px-3 text-white hover:bg-emerald-600"
          data-testid="target-add-btn"
        >
          <Plus size={16}/>
        </Button>
      </form>

      <div className="mt-3 space-y-1.5">
        {loading ? (
          <div className="py-4 text-center text-xs text-zinc-400">Loading…</div>
        ) : total === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-200 py-6 text-center text-xs text-zinc-500 dark:border-zinc-800">
            No targets yet. Add your first focus for today.
          </div>
        ) : (
          items.map((t) => (
            <label
              key={t.target_id}
              className="group flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 transition hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
              data-testid={`target-row-${t.target_id}`}
            >
              <input
                type="checkbox"
                checked={t.completed}
                onChange={() => toggle(t)}
                className="h-4 w-4 shrink-0 cursor-pointer accent-emerald-500"
                data-testid={`target-checkbox-${t.target_id}`}
              />
              <span
                className={`flex-1 text-sm transition ${t.completed ? "text-zinc-400 line-through dark:text-zinc-500" : "text-zinc-800 dark:text-zinc-200"}`}
              >
                {t.content}
              </span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); remove(t); }}
                aria-label="Delete target"
                className="opacity-0 transition group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                data-testid={`target-delete-${t.target_id}`}
              >
                <Trash2 size={14} className="text-zinc-400 hover:text-red-500"/>
              </button>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
